import { useEffect, useState } from "react";
import { Heart, Loader2 } from "lucide-react";
import { Footer } from "@/components/layout/Footer";
import { Navigation } from "@/components/layout/Navigation";
import { Container } from "@/components/ui/Container";
import { DonationCard } from "@/components/modules/donation/DonationPublic";
import { api } from "@/lib/api-client";
import { siteDonationDefaults, type SiteDonationConfig } from "@/lib/site-schema";

type LoadState =
  | { status: "loading" }
  | { status: "ready"; config: SiteDonationConfig }
  | { status: "missing" }
  | { status: "error" };

// Site-wide donation surface. Lives at /donate and is independent of any
// event. The page reads the `site:donation` Key-Value (KV) doc through
// the public `/api/site/donation` endpoint; when the doc is missing or
// `enabled=false`, the page renders a "coming soon" state instead of an
// inert donation card.
export function DonatePage() {
  const [state, setState] = useState<LoadState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    // The server contract for an absent doc is 200 with a `null` body
    // (see functions/api/site/donation.ts onRequestGet). That is the
    // only path that reaches `missing`; a 4xx or 5xx instead throws
    // through the request helper and lands in `error`.
    api.site.donation
      .get()
      .then((config) => {
        if (cancelled) return;
        if (!config) {
          setState({ status: "missing" });
          return;
        }
        setState({ status: "ready", config });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("DonatePage: failed to load site donation config", err);
        setState({ status: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <Navigation
        entries={[
          { label: "Events", to: "/#events" },
          { label: "Donate", to: "/donate" },
        ]}
        homePath="/"
      />
      <main className="flex-1">
        <Body state={state} />
      </main>
      <Footer />
    </>
  );
}

function Body({ state }: { state: LoadState }) {
  if (state.status === "loading") {
    return (
      <Container className="grid h-96 place-items-center text-muted">
        <Loader2 className="h-5 w-5 animate-spin" strokeWidth={1.75} />
      </Container>
    );
  }

  if (state.status === "ready" && state.config.enabled) {
    return <DonationCard config={state.config} idPrefix="site" surface="site" />;
  }

  // Fall through: missing doc, disabled flag, or fetch error all share
  // the same "coming soon" view so we never show an inert donation
  // button. Copy is intentionally soft because the most likely cause is
  // "the admin hasn't published the site donation config yet."
  const defaults = siteDonationDefaults();
  return (
    <section className="mx-auto w-full max-w-3xl px-5 py-20 sm:px-6 lg:px-8 lg:py-28 text-center">
      <p className="eyebrow text-accent inline-flex items-center justify-center gap-2">
        <Heart className="h-3.5 w-3.5" strokeWidth={1.75} />
        Support the Mobeen Center
      </p>
      <h1 className="display-title mt-3 text-[clamp(2rem,5vw,3.4rem)] leading-tight">
        {state.status === "error" ? "We're having trouble loading this page" : defaults.title}
      </h1>
      <p className="mt-4 text-pretty text-muted leading-relaxed">
        {state.status === "error"
          ? "Please refresh in a moment. If this keeps happening, reach out to the Center directly."
          : "Online giving is coming soon. In the meantime, please contact the Center to contribute."}
      </p>
    </section>
  );
}
